/* components/ui/Buttons.jsx — производные кнопки поверх единого Button/btn-хрома.
   IconButton — квадратная кнопка-иконка с tooltip-подписью.
   AddButton  — «+ Добавить» (ghost / dashed для пустых областей).
   Kebab      — «⋮» → Popover с действиями (children = пункты).
   AIButton   — кнопка с CSS-mask логотипом Restorium; состояние busy (pulse).
   BackLink   — «← Назад / ← К реестру» ссылка-навигация.
   ХЕНДОФФ → frontend/components/common/. */

import React from 'react';
import { Button } from './Button.jsx';
import { Icon } from './Icon.jsx';
import { Popover } from './Popover.jsx';
import { useBackNav } from '../../hooks/useBackNav.jsx';
import { RR } from '../../lib/router.jsx';

  const { useNavigate } = RR;

  /* IconButton — квадратная иконо-кнопка. // заменяет: IconBtn */
  const IconButton = React.forwardRef(function IconButton({
    icon, size = 'md', variant = 'ghost', tooltip, label, className, ...rest
  }, ref) {
    return (
      <span className={'icon-btn-wrap' + (tooltip ? ' has-tip' : '')} data-tip={tooltip || undefined}>
        <Button ref={ref} variant={variant} size={size} iconOnly={icon}
                aria-label={label || tooltip} className={className} {...rest} />
      </span>
    );
  });

  /* AddButton — «+ Добавить». dashed=true → пунктирная рамка для пустых зон. */
  function AddButton({ children = 'Добавить', dashed = false, size = 'md', className, ...rest }) {
    const dom = { ...rest };
    ['children', 'dashed', 'size', 'className'].forEach((k) => { delete dom[k]; });
    const cls = ['add-btn', dashed ? 'add-btn--dashed' : '', className || ''].filter(Boolean).join(' ');
    return (
      <button type="button" className={cls} data-size={size} {...dom}>
        <Icon name="plus" size={size === 'sm' ? 14 : 16} />
        <span className="btn__label">{children}</span>
      </button>
    );
  }

  /* Kebab — «⋮» → Popover. children: ({close}) => <PopoverItem.../> */
  function Kebab({ children, label = 'Действия', placement = 'bottom-end', size = 'sm' }) {
    return (
      <Popover placement={placement}
        trigger={<button type="button" className="kebab" aria-label={label}><Icon name="more-vertical" size={18} /></button>}>
        {children}
      </Popover>
    );
  }

  /* AIButton — кнопка с лого-маской Restorium; busy → pulse. */
  function AIButton({ children = 'Спросить ассистента', busy = false, size = 'md', className, ...rest }) {
    const dom = { ...rest };
    ['children', 'busy', 'size', 'className'].forEach((k) => { delete dom[k]; });
    const cls = ['ai-btn', busy ? 'is-busy' : '', className || ''].filter(Boolean).join(' ');
    return (
      <button type="button" className={cls} data-size={size} aria-busy={busy || undefined} {...dom}>
        <span className="ai-btn__glyph" aria-hidden="true" />
        <span className="btn__label">{children}</span>
      </button>
    );
  }

  /* BackLink — навигация назад. to опционально (иначе useBackNav с fallback по маршруту). */
  function BackLink({ to, children = 'Назад', className }) {
    const navigate = useNavigate();
    const goBack = useBackNav ? useBackNav(to) : null;
    const onClick = (e) => { e.preventDefault(); if (goBack) goBack(); else if (to) navigate(to); else navigate(-1); };
    return (
      <a href={to || '#'} className={'back-link' + (className ? ' ' + className : '')} onClick={onClick}>
        <Icon name="arrow-left" size={16} />
        <span>{children}</span>
      </a>
    );
  }

export { IconButton };
export { AddButton };
export { Kebab };
export { AIButton };
export { BackLink };
